import { useMutation } from '@apollo/client';
import * as R from 'ramda';
import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import FormContext from 'common/components/form/FormContext';
import Form from 'components/lib/form/Form';
import FormSubmitButton from 'components/lib/form/FormSubmitButton';
import TextField from 'components/lib/form/TextField';
import LoadingSpinner from 'components/lib/ui/LoadingSpinner';
import OnboardingAbstractField from 'components/onboarding/OnboardingAbstractField';
import type { AbstractFieldChildrenRenderProps } from 'components/onboarding/OnboardingAbstractField';
import OnboardingFormContainer from 'components/onboarding/OnboardingFormContainer';
import OnboardingSurveyRadio from 'components/onboarding/OnboardingSurveyRadio';
import OnboardingTitle from 'components/onboarding/OnboardingTitle';

import {
  GET_USER_REPORTED_ATTRIBUTION_CHANNELS_QUERY,
  SET_USER_REPORTED_ATTRIBUTION_CHANNELS_MUTATION,
} from 'common/lib/graphQl/onboarding';
import useQuery from 'common/lib/hooks/useQuery';
import { spacing } from 'common/lib/theme/dynamic';
import { moveItemToLastByIndex } from 'common/utils/Array';
import { shuffleArray } from 'common/utils/Random';

import { ONBOARDING } from 'common/constants/copy';

import type {
  Common_GetUserReportedOnboardingAttributionChannelsQuery,
  Common_SetUserReportedAttributionChannelsMutation,
} from 'common/generated/graphql';

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-top: ${({ theme }) => theme.spacing.xxxlarge};
`;

const StyledOnboardingSurveyRadio = styled(OnboardingSurveyRadio)`
  padding: ${spacing.xsmall} ${spacing.default};
`;

const OTHER_OPTION_NAME = 'other';
const OTHER_TEXT_INPUT_NAME = 'otherText';

const SubmitButton = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div ref={wrapperRef}>
      <FormSubmitButton size="large">{ONBOARDING.ATTRIBUTION.BUTTON}</FormSubmitButton>
    </div>
  );
};

const getPriorities = (values: Record<string, boolean>) =>
  R.toPairs(values)
    .filter(([, value]) => value)
    .map(([key, value]) =>
      key === OTHER_TEXT_INPUT_NAME && typeof value === 'string' ? value : key,
    );

type Props = {
  next: () => void;
};

const OnboardingAttribution = ({ next }: Props) => {
  const { data: { userReportedOnboardingAttributionChannels = [] } = {}, isLoadingInitialData } =
    useQuery<Common_GetUserReportedOnboardingAttributionChannelsQuery>(
      GET_USER_REPORTED_ATTRIBUTION_CHANNELS_QUERY,
    );
  const [setUserOnboardingAttributionChannels] =
    useMutation<Common_SetUserReportedAttributionChannelsMutation>(
      SET_USER_REPORTED_ATTRIBUTION_CHANNELS_MUTATION,
    );

  const randomizedAttributionChannels = useMemo(() => {
    const shuffledArray = shuffleArray(userReportedOnboardingAttributionChannels);

    // find the "other" option and set it as the last item in the array
    const otherOptionIndex = shuffledArray.findIndex(
      (attributionChannel) => attributionChannel.name === OTHER_OPTION_NAME,
    );

    return moveItemToLastByIndex(shuffledArray, otherOptionIndex);
  }, [userReportedOnboardingAttributionChannels]);

  return isLoadingInitialData ? (
    <StyledLoadingSpinner />
  ) : (
    <>
      <OnboardingTitle>{ONBOARDING.ATTRIBUTION.TITLE}</OnboardingTitle>
      <OnboardingFormContainer>
        <Form
          onSubmit={async (values: Record<string, boolean | string>) => {
            const priorities = R.toPairs(values)
              .filter(([, value]) => value)
              .map(([key, value]) =>
                key === OTHER_TEXT_INPUT_NAME && typeof value === 'string' ? value : key,
              )
              .filter(Boolean); // filter out empty strings when user skips the other field

            await setUserOnboardingAttributionChannels({
              variables: {
                priorities,
              },
            });
          }}
          onSubmitSuccess={() => next()}
        >
          {randomizedAttributionChannels.map(({ name, displayName }) => (
            <OnboardingAbstractField name={name} hideLabel key={name}>
              {({ value, setFieldValue, handleSubmit }: AbstractFieldChildrenRenderProps) => (
                <StyledOnboardingSurveyRadio
                  value={value}
                  hideIndicator
                  onChange={(value) => {
                    // unselect all attribution channels so only one is selected at a time
                    randomizedAttributionChannels.forEach((attributionChannel) => {
                      setFieldValue(attributionChannel.name, false);
                    });
                    setFieldValue(name, value);
                    if (name !== OTHER_OPTION_NAME) {
                      setFieldValue(OTHER_TEXT_INPUT_NAME, undefined);
                      handleSubmit();
                    }
                  }}
                >
                  {displayName}
                </StyledOnboardingSurveyRadio>
              )}
            </OnboardingAbstractField>
          ))}
          <FormContext.Consumer>
            {({ values }) => {
              const [mainPriority] = getPriorities(values);

              return mainPriority === OTHER_OPTION_NAME ? (
                <>
                  <TextField
                    label=""
                    name={OTHER_TEXT_INPUT_NAME}
                    placeholder={ONBOARDING.ATTRIBUTION.OTHER_INPUT_PLACEHOLDER}
                    autoFocus
                  />

                  <SubmitButton />
                </>
              ) : null;
            }}
          </FormContext.Consumer>
        </Form>
      </OnboardingFormContainer>
    </>
  );
};

export default OnboardingAttribution;
